import React, { useContext, useMemo } from "react";
import { useTextDomainContext } from "@/app/providers";
import { string, func, bool, oneOf, object } from "prop-types";
import { useGetGroups } from "@/hooks";
import { SelectDropdown } from "@/components";

type SubsidiaryDropdownOption = {
  label?: string;
  value?: string;
  [key: string]: unknown;
};

type SubsidiaryDropdownProps = {
  componentLabel?: string;
  defaultValue?: string;
  onChange?: (event: SubsidiaryDropdownOption) => void;
  showAll?: boolean;
  visualStyle?: "autoComplete" | "autoCompleteMulti" | "simple" | "old";
  sx?: Record<string, unknown>;
  currentSubsidiary?: string;
  currentTeamId?: string;
  required?: boolean;
  refetched?: () => void;
  refetch?: boolean;
  cacheKey?: string;
};

/**
 * Customer dropdown component
 * @param {object} props props
 * @returns {Element} React Component
 * @class
 */
export const SubsidiaryDropdown = (props: SubsidiaryDropdownProps) => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const { currentTeamId, showAll } = props;
  // console.log(currentTeamId);

  // const { customers: allCompanies } = useCompanies({
  //   customerId: currentTeamId,
  // });

  // const currentTeamId = useMemo(() => {
  //   return allCompanies[0]?.id || "";
  // }, [allCompanies]);

  // const currentTeam = useMemo(() => {
  //   return allCompanies[0]?.name || "";
  // }, [allCompanies]);

  // console.log(currentTeam);

  const { subsidiary: selectedCompanies } = useGetGroups({
    customerId: currentTeamId,
  });

  //#region useMemo
  const componentLabel = useMemo(() => {
    return props.componentLabel || gettext("Child Company");
  }, [gettext, props.componentLabel]);

  const options = useMemo(() => {
    // console.log(selectedCompanies);
    const customerArray: SubsidiaryDropdownOption[] = [];
    selectedCompanies.map((customerRow) => {
      const oneCustomer = {
        label: customerRow.name === "All" ? gettext("All") : customerRow.name,
        value: customerRow.id,
        key:
          customerRow.id === "all"
            ? "all"
            : customerRow.key ||
              (customerRow.country && customerRow.cvr
                ? `${customerRow.country.toUpperCase()}${customerRow.cvr}`
                : customerRow.id),
      };
      customerArray.push(oneCustomer);
      return true;
    });

    // console.log(currentTeam, customerArray);

    return customerArray;
  }, [gettext, selectedCompanies]);

  const defaultValue = useMemo(() => {
    let returnValue = "";

    // Special case: if defaultValue is "all" and showAll is true, use "all"
    if (props?.defaultValue === "all" && showAll) {
      returnValue = "all";
      return returnValue;
    }

    // First try to match by ID or key
    if (!returnValue && props?.defaultValue) {
      const target = String(props.defaultValue).trim().toUpperCase();
      const foundValue = options.find(
        (option) =>
          option.value === props?.defaultValue ||
          String((option as any).key || "")
            .trim()
            .toUpperCase() === target ||
          option.label.toUpperCase() === target,
      );
      if (foundValue) {
        returnValue = foundValue.value;
      }
    }

    // If ID doesn't match but we have a currentSubsidiary name, try matching by name
    if (
      !returnValue &&
      props?.currentSubsidiary &&
      props?.currentSubsidiary !== "All"
    ) {
      const foundByName = options.find(
        (option) => option.label === props.currentSubsidiary,
      );
      if (foundByName) {
        returnValue = foundByName.value;
      }
    }

    // If still no match and showAll is true, use "all"
    if (!returnValue && showAll) {
      returnValue = "all";
    }

    // Last resort: use first option
    if (!returnValue && options.length) {
      returnValue = options[0]?.value;
    }

    return returnValue;
  }, [options, props.defaultValue, props.currentSubsidiary, showAll]);
  //#endregion useMemo

  //#region Handlers
  const handleChange = (event: any) => {
    if (typeof props.onChange === "function") {
      props.onChange(event as any);
    }
  };
  //#endregion Handlers

  return (
    <>
      <SelectDropdown
        options={
          options as unknown as import("@/types/Dropdown.types").DropdownOption[]
        }
        componentLabel={componentLabel}
        defaultValue={defaultValue}
        cacheKey={props.cacheKey}
        showAll={showAll}
        required={props.required}
        onChange={handleChange}
        refetch={props.refetch || false}
        refetched={props.refetched as any}
        visualStyle="autoComplete"
        sx={{
          ...props.sx,
        }}
      />
    </>
  );
};

SubsidiaryDropdown.propTypes = {
  componentLabel: string,
  defaultValue: string,
  onChange: func,
  showAll: bool,
  visualStyle: oneOf(["autoComplete", "autoCompleteMulti", "simple", "old"]),
  sx: object,
  currentSubsidiary: string,
  // currentTeam: string,
  currentTeamId: string,
  required: bool,
  refetched: func,
  refetch: bool,
  cacheKey: string,
};
